import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  ActivatedRoute,
  IsActiveMatchOptions,
  Router,
  UrlTree,
} from '@angular/router';

/**
 * Configuration for one tab inside `<app-navigation-tabs>`.
 *
 * Active-state highlighting (`btnChange` class) is driven by EITHER:
 *   - `routerLink` + `routerLinkActive` — the recommended pattern when the
 *     tab navigates to a real route.
 *   - `activeCondition` — for in-page tab switches with no routing. The
 *     parent component is responsible for keeping this in sync (e.g. on
 *     `(tabClick)` mutate the array so exactly one tab is active).
 *
 * Click handling: a tab with `routerLink` will navigate AND emit
 * `(tabClick)`. A tab without `routerLink` only emits `(tabClick)`. Do not
 * use both routerLink and an in-page tabClick handler that also navigates,
 * or the navigation will fire twice.
 *
 * A tab whose `routerLink` is a strict prefix of any sibling tab's URL
 * must set `routerLinkActiveOptions` to opt into exact-path matching;
 * otherwise Angular's default prefix-match will keep the parent tab
 * highlighted alongside the active sub-tab.
 */
export interface NavigationTab {
  label: string;
  /** Set to enable router-driven activation. Pair with routerLinkActive. */
  routerLink?: string | any[];
  /** Class to apply when the routerLink matches. Usually 'btnChange'. */
  routerLinkActive?: string;
  /**
   * Optional override for Angular's `routerLinkActive` match strictness.
   * Defaults to undefined, in which case Angular's built-in prefix match
   * applies. Pass the full `IsActiveMatchOptions` shape (preferred over
   * the legacy `{ exact: true }`) when query-param behavior must be
   * explicit.
   */
  routerLinkActiveOptions?: { exact: boolean } | IsActiveMatchOptions;
  iconClass?: string;
  value?: string;
  /**
   * Optional explicit automation testid for this tab's button. When set it
   * overrides the default `nav-tab-<value>` testid. Left undefined for tabs
   * that are happy with the generated default.
   */
  dataTestId?: string;
  helpTitle?: string;
  hidden?: boolean;
  /** Manual active flag, used when the tab has no routerLink. */
  activeCondition?: boolean;
  /**
   * Class(es) applied when this tab is active, overriding the default
   * `btnChange`. Lets a screen give a tab a different highlight colour
   * (e.g. the Orders trackers use turquoise, completed/rejected use grey).
   * Space-separated string; falls back to `btnChange` when unset.
   */
  activeClass?: string;
}

@Component({
  selector: 'app-navigation-tabs',
  templateUrl: './navigation-tabs.component.html',
  styleUrls: ['./navigation-tabs.component.scss'],
})
export class NavigationTabsComponent implements OnInit, OnChanges {
  @Input() tabs: NavigationTab[] = [];
  /**
   * Extra class(es) applied to every tab button of THIS instance — a
   * per-screen geometry hook. The Orders and Reservations status strips pass
   * 'px-5' to keep develop's wide buttons; screens that omit it keep the
   * compact default.
   */
  @Input() buttonClass = '';
  @Output() tabClick = new EventEmitter<NavigationTab>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.ensureDefaultActive();
  }

  ngOnChanges(): void {
    this.ensureDefaultActive();
  }

  onTabClick(tab: NavigationTab) {
    this.tabClick.emit(tab);
  }

  hasRouterLink(tab: NavigationTab): boolean {
    return tab.routerLink !== undefined && tab.routerLink !== null;
  }

  isTabHighlighted(tab: NavigationTab): boolean {
    if (tab.activeCondition) {
      return true;
    }
    return this.isRouterTabActive(tab);
  }

  private isRouterTabActive(tab: NavigationTab): boolean {
    if (!tab.routerLink) {
      return false;
    }
    const tabUrlTree = this.toUrlTree(tab.routerLink);
    if (!tabUrlTree) {
      return false;
    }
    return this.router.isActive(
      tabUrlTree,
      this.toIsActiveMatchOptions(tab.routerLinkActiveOptions),
    );
  }

  private toUrlTree(routerLink: string | any[]): UrlTree | null {
    if (Array.isArray(routerLink)) {
      return this.router.createUrlTree(routerLink, {
        relativeTo: this.activatedRoute,
      });
    }
    if (typeof routerLink === 'string') {
      if (routerLink.trim().length === 0) {
        return this.router.createUrlTree([], {
          relativeTo: this.activatedRoute,
        });
      }
      if (routerLink.startsWith('/')) {
        return this.router.parseUrl(routerLink);
      }
      return this.router.createUrlTree([routerLink], {
        relativeTo: this.activatedRoute,
      });
    }
    return null;
  }

  private toIsActiveMatchOptions(
    options?: { exact: boolean } | IsActiveMatchOptions,
  ): IsActiveMatchOptions {
    if (!options) {
      return {
        paths: 'subset',
        queryParams: 'subset',
        matrixParams: 'subset',
        fragment: 'ignored',
      };
    }
    if ('exact' in options) {
      return options.exact
        ? {
            paths: 'exact',
            queryParams: 'exact',
            matrixParams: 'exact',
            fragment: 'ignored',
          }
        : {
            paths: 'subset',
            queryParams: 'subset',
            matrixParams: 'subset',
            fragment: 'ignored',
          };
    }
    return options;
  }

  /**
   * Guarantee that in-page (non-router) tab sets show a highlighted tab on
   * initial render. When no tab is router-driven and none has been marked
   * active by the parent, default the first visible tab to active. Pages
   * that drive highlighting through `routerLinkActive` (any tab carries a
   * `routerLink`) are left untouched, and a tab the parent has already
   * pre-selected (e.g. a deep link landing on a non-first tab) is preserved.
   *
   * Without this, pages whose tabs have no `routerLink` and whose parent
   * only sets `activeCondition` on `(tabClick)` rendered with no tab
   * highlighted until the user clicked one (regression from the migration
   * of bespoke per-page tab buttons onto this shared component).
   */
  private ensureDefaultActive(): void {
    if (!this.tabs?.length) {
      return;
    }
    const isRouterDriven = this.tabs.some((tab) => !!tab.routerLink);
    if (isRouterDriven) {
      return;
    }
    const alreadyActive = this.tabs.some((tab) => tab.activeCondition);
    if (alreadyActive) {
      return;
    }
    const firstVisible = this.tabs.find((tab) => !tab.hidden);
    if (firstVisible) {
      firstVisible.activeCondition = true;
    }
  }
}
